import React, { useMemo, useState } from 'react';
import { Modal, Descriptions, Empty, Switch } from 'antd';
import { IClientJob, ClientJobTableColumns } from '@/ts/common-types';
import { formatCronString } from '@/ts/utils';
import '@/scss/job-history-modal.scss';
import { useUserTimezone } from '@/hooks/AllCustomHooks';
import { convertIsoDate } from '@/ts/utils';
import { DateTimeFormats } from '@/ts/constants';

interface IClientJobHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    job: IClientJob | null;
}

const ClientJobHistoryModal: React.FC<IClientJobHistoryModalProps> = ({ isOpen, onClose, job }) => {
    const [showLocalTimezone, setShowLocalTimezone] = useState(false);
    const currentTimeZone = useUserTimezone();

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        
        try {
            if (showLocalTimezone) {
                return convertIsoDate(dateString, currentTimeZone);
            } else {
                if (job?.schedule_timezone) {
                    return convertIsoDate(dateString, job.schedule_timezone);
                } else {
                    return `${convertIsoDate(dateString)} ${DateTimeFormats.UTC_SUFFIX}`;
                }
            }
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid Date';
        }
    };

    const basicDetails = useMemo(() => job ? [
        { label: ClientJobTableColumns.CLIENT, children: job.client_name },
        { label: ClientJobTableColumns.CHANNEL, children: job.channel_name },
        { label: ClientJobTableColumns.ID, children: job.id },
        { label: ClientJobTableColumns.SOURCE, children: job.source },
        { label: ClientJobTableColumns.REPORT_NAME, children: job.report_name },
        { label: ClientJobTableColumns.PROGRAM_TYPE, children: job.program_type || 'N/A' },
    ] : [], [job]);

    const scheduleDetails = useMemo(() => job ? [
        { label: ClientJobTableColumns.PERIOD, children: job.period },
        { label: ClientJobTableColumns.PRIORITY, children: job.job_priority },
        { label: ClientJobTableColumns.SCHEDULE, children: formatCronString(job.cron_string) },
        { label: ClientJobTableColumns.JOB_CLASS, children: job.job_class },
        { label: ClientJobTableColumns.RECORD_TYPE, children: job.record_type || 'N/A' },
        { label: ClientJobTableColumns.SCHEDULE_TIMEZONE, children: job.schedule_timezone || 'N/A' },
    ] : [], [job]);

    const executionDetails = useMemo(() => job ? [
        { 
            label: ClientJobTableColumns.LAST_EXECUTION_STATUS, 
            children: job.last_execution_status,
            className: `status-${job.last_execution_status?.toLowerCase()}`
        },
        { 
            label: ClientJobTableColumns.LAST_STAGE_STATUS, 
            children: job.last_stage_status 
        }
    ] : [], [job]);

    const timingDetails = useMemo(() => job ? [
        { 
            label: ClientJobTableColumns.LAST_EXECUTION_DATE, 
            children: formatDateTime(job.last_execution_date)
        },
        { 
            label: ClientJobTableColumns.LAST_REPORT_DATE, 
            children: formatDateTime(job.last_report_date)
        },
        { 
            label: ClientJobTableColumns.LAST_UPDATE, 
            children: formatDateTime(job.last_execution_timestamp)
        },
        { 
            label: ClientJobTableColumns.EXECUTION_DURATION, 
            children: job.last_execution_duration || 'N/A' 
        },
    ] : [], [job, showLocalTimezone]);

    const trackingDetails = useMemo(() => job ? [
        { label: ClientJobTableColumns.TASK_UID, children: job.task_uid || 'N/A' },
        { label: ClientJobTableColumns.GROUP_UID, children: job.group_uid || 'N/A' },
        { 
            label: ClientJobTableColumns.CREATED_ON, 
            children: formatDateTime(job.created_on)
        },
        { 
            label: ClientJobTableColumns.MODIFIED_ON, 
            children: formatDateTime(job.modified_on)
        },
    ] : [], [job, showLocalTimezone]);

    if (!job) return null;

    return (
        <Modal
            title={
                <div className="modal-title-section py-2">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="title mb-2">Job History</h5>
                            <h6 className="subtitle mb-0">
                                <span className="fw-bold">{job.id}</span> - {job.report_name || 'N/A'}
                            </h6>
                        </div>
                        <div className="timezone-switch-container d-flex align-items-center me-4">
                            <span className="me-2 text-muted">Show timestamps in local timezone</span>
                            <Switch
                                checked={showLocalTimezone}
                                onChange={setShowLocalTimezone}
                                size="small"
                            />
                        </div>
                    </div>
                </div>
            }
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={1000}
            className="job-history-modal"
        >
            <div className="modal-body-content py-3">
                <div className="card shadow-sm mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <span className="header-title">Basic Information</span>
                        <span className={`status-badge ${job.status ? 'active' : 'inactive'}`}>
                            {job.status ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div className="card-body">
                        <Descriptions bordered column={3} size="small">
                            {basicDetails.map(({ label, children }) => (
                                <Descriptions.Item key={label} label={label}>{children}</Descriptions.Item>
                            ))}
                        </Descriptions>
                    </div>
                </div>

                <div className="card shadow-sm mb-4">
                    <div className="card-header">
                        <span className="header-title">Schedule Configuration</span>
                    </div>
                    <div className="card-body">
                        <Descriptions bordered column={3} size="small">
                            {scheduleDetails.map(({ label, children }) => (
                                <Descriptions.Item key={label} label={label}>{children}</Descriptions.Item>
                            ))}
                        </Descriptions>
                    </div>
                </div>

                <div className="card shadow-sm mb-4">
                    <div className="card-header">
                        <span className="header-title">Latest Execution Status</span>
                    </div>
                    <div className="card-body">
                        {job.last_execution_status ? (
                            <>
                                <Descriptions bordered column={3} size="small" className="mb-3">
                                    {executionDetails.map(({ label, children, className }) => (
                                        <Descriptions.Item key={label} label={label} className={className}>
                                            {children}
                                        </Descriptions.Item>
                                    ))}
                                </Descriptions>
                                <Descriptions bordered column={2} size="small">
                                    {timingDetails.map(({ label, children }) => (
                                        <Descriptions.Item key={label} label={label}>{children}</Descriptions.Item>
                                    ))}
                                </Descriptions>
                            </>
                        ) : (
                            <div className="empty-state">
                                <Empty 
                                    description={
                                        <div className="empty-text">
                                            <p className="primary-text">No execution history available</p>
                                            <p className="secondary-text mb-0">Job execution details will appear here</p>
                                        </div>
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="card shadow-sm">
                    <div className="card-header">
                        <span className="header-title">Tracking Information</span>
                    </div>
                    <div className="card-body">
                        <Descriptions bordered column={2} size="small">
                            {trackingDetails.map(({ label, children }) => (
                                <Descriptions.Item key={label} label={label}>{children}</Descriptions.Item>
                            ))}
                        </Descriptions>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ClientJobHistoryModal; 